import { useEffect, useState } from 'react';

import { LineupMember } from 'features/common/components/Punchup/composites/tiles/PunchupHighlightsCollection';
import { isEmbeddedInIframe } from 'utils/utils';

/**
 * Narrows one entry of the untrusted `comedians-updated` payload. An entry missing an id or a name
 * is dropped rather than rendered as a blank disc; a missing/malformed `imageUrl` becomes `null`,
 * which the card renders as initials.
 */
const toLineupMember = (raw: unknown): LineupMember | null => {
	if (typeof raw !== 'object' || raw === null) return null;
	const { id, name, imageUrl } = raw as { id?: unknown; name?: unknown; imageUrl?: unknown };
	if (typeof id !== 'string' && typeof id !== 'number') return null;
	if (typeof name !== 'string' || name.trim() === '') return null;
	return { id, name, imageUrl: typeof imageUrl === 'string' ? imageUrl : null };
};

/**
 * Listens for `comedians-updated` from the PunchupLive host — the show's lineup, which lives in the
 * host and has no tixologi API of its own. The host pushes it once after our `dashboard-ready` and
 * again on every lineup edit (see `docs/iframe-communication.md`).
 *
 * Returns `null` until the first payload arrives, and the latest array after that. `null` is the
 * version-skew guard: a host that predates this protocol simply never pushes, the caller renders no
 * lineup tile, and the dashboard looks exactly as it does today. An empty lineup is `[]`, not
 * `null` — the tile still renders so the jump to the Comedians section stays available.
 */
export const useComediansFromHost = (): LineupMember[] | null => {
	const [comedians, setComedians] = useState<LineupMember[] | null>(null);

	useEffect(() => {
		const handler = (event: MessageEvent) => {
			// Same two guards every parent→iframe listener here uses: standalone tixologist has no
			// host, and only the embedding parent may speak for it.
			if (!isEmbeddedInIframe()) return;
			if (event.source !== window.parent) return;
			if (event.data?.action !== 'comedians-updated') return;

			const raw = event.data?.comedians;
			if (!Array.isArray(raw)) return;

			setComedians(raw.map(toLineupMember).filter((member): member is LineupMember => member !== null));
		};

		window.addEventListener('message', handler);
		return () => window.removeEventListener('message', handler);
	}, []);

	return comedians;
};
